/**
 * Shared constants and pure helpers for parent AI assistant topic categories and severity.
 * Used by parent-ai-assistant and ai-assistant-header.
 */

export const CATEGORY_KEYS: Record<string, string> = {
  behavior_regulation:
    'app.components.parentAiAssistant.categories.behavior_regulation',
  emotional_outbursts:
    'app.components.parentAiAssistant.categories.emotional_outbursts',
  screen_time: 'app.components.parentAiAssistant.categories.screen_time',
  homework_struggles:
    'app.components.parentAiAssistant.categories.homework_struggles',
  sleep: 'app.components.parentAiAssistant.categories.sleep',
  anxiety_worry: 'app.components.parentAiAssistant.categories.anxiety_worry',
  friendships: 'app.components.parentAiAssistant.categories.friendships',
  sibling_relations:
    'app.components.parentAiAssistant.categories.sibling_relations',
  defiance_conflict:
    'app.components.parentAiAssistant.categories.defiance_conflict',
  school_performance:
    'app.components.parentAiAssistant.categories.school_performance',
  bullying: 'app.components.parentAiAssistant.categories.bullying',
  gaming: 'app.components.parentAiAssistant.categories.gaming',
  social_media: 'app.components.parentAiAssistant.categories.social_media',
  online_safety: 'app.components.parentAiAssistant.categories.online_safety',
  puberty: 'app.components.parentAiAssistant.categories.puberty',
  independence: 'app.components.parentAiAssistant.categories.independence',
  responsibility: 'app.components.parentAiAssistant.categories.responsibility',
  critical_safety:
    'app.components.parentAiAssistant.categories.critical_safety',
  general: 'app.components.parentAiAssistant.categories.general',
  duties: 'app.components.parentAiAssistant.categories.duties',
  interests: 'app.components.parentAiAssistant.categories.interests',
  courses: 'app.components.parentAiAssistant.categories.courses',
};

export const CATEGORY_ICONS: Record<string, string> = {
  behavior_regulation: '🧠',
  emotional_outbursts: '😤',
  screen_time: '📱',
  homework_struggles: '📚',
  sleep: '😴',
  anxiety_worry: '😰',
  friendships: '👥',
  sibling_relations: '👫',
  defiance_conflict: '⚡',
  school_performance: '📖',
  bullying: '🛡️',
  gaming: '🎮',
  social_media: '💬',
  online_safety: '🔒',
  puberty: '🌱',
  independence: '🦋',
  responsibility: '✅',
  critical_safety: '🆘',
  general: '💬',
  duties: '✅',
  interests: '🌟',
  courses: '📚',
};

export const SEVERITY_COLORS: Record<string, string> = {
  low: '#11AB5E',
  medium: '#FF9F0A',
  high: '#f14646',
  critical: '#B33E4C',
};

/** Persisted in `localStorage` (last selected kid for parent AI). */
export const STORAGE_KEY_SELECTED_CHILD = 'parent-ai-selected-child-id';

/** Stored when parent chose a general (non–kid-specific) AI thread. */
export const NEUTRAL_CHILD_STORAGE_VALUE = '__neutral__';

/** `kidAwaitingTopicSync` sentinel for neutral selection (not a real kid id). */
export const KID_TOPIC_SYNC_NEUTRAL = '__neutral__';

const GENERAL_CATEGORY_KEY =
  'app.components.parentAiAssistant.categories.general';

export function getCategoryLabelKey(category: string): string {
  return CATEGORY_KEYS[category] ?? GENERAL_CATEGORY_KEY;
}

export function getCategoryIcon(category: string): string {
  return CATEGORY_ICONS[category] ?? '💬';
}

export function getSeverityColor(severity: string): string {
  return SEVERITY_COLORS[severity] ?? SEVERITY_COLORS['medium'];
}

const TIME_AGO_BASE = 'app.components.parentAiAssistant.timeAgo';

export type TranslateInstant = (
  key: string,
  params?: Record<string, unknown>,
) => string;

/** Expects Unix timestamp in seconds (as returned by the API). */
export function formatTimeAgo(ts: number, instant: TranslateInstant): string {
  const date = new Date(ts * 1000);
  const now = Date.now();
  const diff = now - date.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (hours < 1) return instant(`${TIME_AGO_BASE}.justNow`);
  if (hours < 24) return instant(`${TIME_AGO_BASE}.hoursAgo`, { count: hours });
  if (days === 1) return instant(`${TIME_AGO_BASE}.yesterday`);
  return instant(`${TIME_AGO_BASE}.daysAgo`, { count: days });
}
